/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { SkeletonText } from 'src/components/ts-components/SkeletonBlock'
import {
  OPERATIONS_GRID_HEADER_HEIGHT,
  OPERATIONS_GRID_ROW_HEIGHT,
  operationsGridStyle,
} from '../operationsGridStyle'

/** The real grid's row height, imported rather than restated so the skeleton's
 * footprint cannot drift from the table it stands in for — a mismatch here is
 * exactly the layout jump the skeleton exists to prevent. */
const SKELETON_ROW_HEIGHT = OPERATIONS_GRID_ROW_HEIGHT

/** Enough rows to read as "a table", without pretending to know the real
 * count — a fixed number, not driven by data. */
const SKELETON_ROW_COUNT = 5

interface RosterGridSkeletonProps {
  columnCount: number
  testId: string
}

/**
 * Stands in for `RosterGrid` while its rows are loading. Replaces ag-grid's
 * own spinner-over-empty-grid overlay with a skeleton shaped like the table
 * that is about to render, so the panel does not visibly "pop" between a
 * spinner and real rows.
 *
 * A sibling of `RosterGrid` rather than a branch inside it: it shares only
 * the outer panel styling (`operationsGridStyle`, header height), none of
 * ag-grid's row/column machinery, so keeping it separate avoids threading an
 * unused `loading` fork through the DataGrid wiring.
 */
const RosterGridSkeleton = ({
  columnCount,
  testId,
}: RosterGridSkeletonProps) => {
  'use memo'

  const columns = Array.from({ length: columnCount })
  const rows = Array.from({ length: SKELETON_ROW_COUNT })

  return (
    <div css={operationsGridStyle} data-testid={testId} aria-hidden>
      <div css={frameStyle}>
        <div css={headerRowStyle}>
          {columns.map((_, columnIndex) => (
            <SkeletonText key={columnIndex} width='60%' height={10} />
          ))}
        </div>
        {rows.map((_, rowIndex) => (
          <div css={bodyRowStyle} key={rowIndex}>
            {columns.map((_, columnIndex) => (
              <SkeletonText key={columnIndex} width='80%' height={12} />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

const frameStyle = css`
  width: 100%;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
  overflow: hidden;
`

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 12px;
  box-sizing: border-box;

  > span {
    flex: 1;
    min-width: 0;
  }
`

/* Mirrors `dataGridTheme`: the header sits level with the first row rather
   than on a darker band, and the frame/divider hairlines match the theme's. */
const headerRowStyle = css`
  ${rowStyle}
  height: ${OPERATIONS_GRID_HEADER_HEIGHT}px;
  background: ${colors.SECONDARY_300};
  border-bottom: solid 1px ${colors.SECONDARY_200};
`

/* Zebra, matching the theme: AG Grid counts from zero, so the SECOND row is
   the stepped-down one. `nth-child` is 1-based, hence `even`. */
const bodyRowStyle = css`
  ${rowStyle}
  height: ${SKELETON_ROW_HEIGHT}px;
  background: ${colors.SECONDARY_300};

  &:nth-of-type(even) {
    background: ${colors.SECONDARY_400};
  }

  & + & {
    border-top: solid 1px ${colors.SECONDARY_200};
  }
`

export default RosterGridSkeleton
